import { BaseCpFormModel, ControlProps, WrappedObjectsEnum } from "../../../../models/form/base-cp-form.model";
import { JobTemplateModel } from "../../../../models/job_template/job-template.model";
import { UntypedFormGroup } from "@angular/forms";
import { JobRoutineBasicInfoFormModel } from "./job-routine-basic-info-form.model";
import { JobRoutineTimeFormModel } from "./job-routine-time-form.model";
import { JobRoutineFilesFormModel } from "./job-routine-files-form.model";
import { JobTemplateCreationModel } from "../../../../models/job_template/job-template-creation.model";
import { JobUtils } from "../../../../utils/JobUtils";
import TargetEntityType from "../../../../enums/frontend/TargetEntityType";
import { DocumentGroupReferenceType } from "../../../../enums/document/DocumentGroupReferenceType";
import { DocumentGroupModel } from "../../../../models/document/document-group.model";
import Languages from "../../../../../assets/i18n/Languages";
import { DocumentModel } from "../../../../models/document/document.model";
import { JobTemplateUpdateModel } from "../../../../models/job_template/job-template-update.model";
import { AssigneeEntityType } from "../../../../enums/frontend/AssigneeEntityType";
import { JobTemplateConditionType } from "../../../../enums/job_template/JobTemplateConditionType";
import AccessMode from '../../../../enums/organization/AccessMode';
import { getCurrentDocumentsFromGroup } from '../../../../models/document/shared.document-model.util';

export class JobRoutineFormModel extends BaseCpFormModel<JobRoutineFormModel, JobTemplateModel> {

  //region FIELDS
  static readonly INFOS = 'infos';
  static readonly TIME = 'time';
  static readonly FILES = 'files';


  infos: JobRoutineBasicInfoFormModel;
  infosFormGroup: UntypedFormGroup;
  time: JobRoutineTimeFormModel;
  timeFormGroup: UntypedFormGroup;
  files: JobRoutineFilesFormModel;
  filesFromGroup: UntypedFormGroup;
  triggersFormGroup: UntypedFormGroup;


  form: UntypedFormGroup;

  //endregion FIELDS

  constructor(model: JobTemplateModel | null = null, accessMode: AccessMode) {
    super();

    this.createForm(this, model, accessMode);
  }

  asFormGroup(): UntypedFormGroup {
    this.infosFormGroup = this.infos.asFormGroup();
    this.form.addControl(JobRoutineFormModel.INFOS, this.infosFormGroup);

    this.timeFormGroup = this.time.asFormGroup();
    this.form.addControl(JobRoutineFormModel.TIME, this.timeFormGroup);

    this.filesFromGroup = this.files.asFormGroup();
    this.form.addControl(JobRoutineFormModel.FILES, this.filesFromGroup);

    return this.form;
  }

  asModel(formGroup: UntypedFormGroup, model: JobTemplateModel): JobTemplateModel {

    this.infos.asModel(formGroup.get(JobRoutineFormModel.INFOS) as UntypedFormGroup, model);
    this.time.asModel(formGroup.get(JobRoutineFormModel.TIME) as UntypedFormGroup, model);
    this.files.asModel(formGroup.get(JobRoutineFormModel.FILES) as UntypedFormGroup, model.documents);
    return model;
  }

  createForm(formModel: JobRoutineFormModel, model: JobTemplateModel, accessMode?: AccessMode): JobRoutineFormModel {
    const infoFormModel = new JobRoutineBasicInfoFormModel();
    formModel.infos = infoFormModel.createForm(infoFormModel, model, accessMode);

    const timeFormModel = new JobRoutineTimeFormModel();
    formModel.time = timeFormModel.createForm(timeFormModel, model);

    const filesFormModel = new JobRoutineFilesFormModel();
    formModel.files = filesFormModel.createForm(filesFormModel, model?.documents);

    if (model?.jobTemplateConditionType) {
      let order = 1;
      model.triggerData?.forEach(tD => {
        tD.isThenTrigger = model.jobTemplateConditionType == JobTemplateConditionType.THEN;
        tD.isFirstThenTrigger = order == 1;
        order++;
      });
    }

    formModel.form = new UntypedFormGroup({});
    return formModel;
  }

  disableForm(): void {
    this.form.disable();
  }

  enableForm(): void {
    this.form.enable();
  }

  /**
   * Takeover the values of the template.
   *
   * Listing:
   * <ol>
   *   <li><h3>Basic Infos:</h3>
   *   <h4>Included values:</h4>
   *      <ol>
   *        <li>ACTIVE</li>
   *        <li>NAME</li>
   *        <li>IS_PINNED</li>
   *        <li>FLAT_RATE</li>
   *        <li>PRODUCER_NUMBER</li>
   *        <li>CATEGORY</li>
   *        <li>DESCRIPTION</li>
   *        <li>EXECUTION_LOCATION_EXTERN</li>
   *        <li>RESPONSIBILITY_EXTERN</li>
   *        <li>SERVICE_PROVIDER</li>
   *        <li>JOB_BEGIN_STATUS</li>
   *      </ol>
   *    <h4>Excluded values:</h4>
   *      <ol>
   *        <li>TARGET_TYPE</li>
   *        <li>TARGET</li>
   *        <li>ASSIGNEE_TYPE</li>
   *        <li>ASSIGNED_USER</li>
   *        <li>ASSIGNED_WORK_GROUP</li>
   *      </ol>
   *   </li>
   *   <li><h3>Time:</h3>
   *      <h4>Included values:</h4>
   *      <ol>
   *        <li>TIME_EXECUTION_DURATION_AMOUNT</li>
   *        <li>TIME_EXECUTION_DURATION_UNIT</li>
   *        <li>TIME_STAND_STILL_DURATION_AMOUNT</li>
   *        <li>TIME_STAND_STILL_DURATION_UNIT</li>
   *        <li>TIME_IS_IDENTICAL</li>
   *        <li>TIME_STAND_STILL</li>
   *      </ol>
   *      <h4>Excluded values:</h4>
   *      <ol>
   *        <li>TIME_IS_IDENTICAL_PREV_VALUE</li>
   *      </ol>
   *   </li>
   *   <li><h3>Files:</h3> everything</li>
   *   <li><h3>Triggers:</h3> everything</li>
   * </ol>
   * @param jobTemplate
   */
  fillInfosFromTemplate(jobTemplate: JobTemplateModel): void {
    this.fillBasicInfos(jobTemplate);
    this.fillTimeInfos(jobTemplate);
    this.fillDocumentInfos(jobTemplate);
  }

  private fillDocumentInfos(jobTemplate: JobTemplateModel) {
    if (jobTemplate?.documents) {
      const documentsProps = ControlProps.of(JobRoutineFilesFormModel.DOCUMENTS);
      documentsProps.withDefaultValue(this.files.documentsControl.value);
      documentsProps.withValue(getCurrentDocumentsFromGroup(jobTemplate.documents));
      this.files.setControlValue(documentsProps);
    }
  }

  private fillTimeInfos(jobTemplate: JobTemplateModel) {
    if (jobTemplate?.plannedWorkingTime) {
      const executionDuration = JobRoutineTimeFormModel.getDurationUnitAndAmount(jobTemplate?.plannedWorkingTime);
      const executionDurationAmountProps: ControlProps = ControlProps.of(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT);
      executionDurationAmountProps.withDefaultValue(this.time.workTimeDurationAmountControl.value);
      executionDurationAmountProps.withValue(jobTemplate?.plannedWorkingTime ? executionDuration.time : null);
      this.time.setControlValue(executionDurationAmountProps);

      const executionDurationUnitProps: ControlProps = ControlProps.of(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_UNIT);
      executionDurationUnitProps.withDefaultValue(this.time.workTimeDurationUnitControl.value);
      executionDurationUnitProps.withValue(jobTemplate?.plannedWorkingTime ? executionDuration.unit : null);
      this.time.setControlValue(executionDurationUnitProps);
    }

    if (jobTemplate?.plannedImplDuration) {
      const plannedImplDuration = JobRoutineTimeFormModel.getDurationUnitAndAmount(jobTemplate?.plannedImplDuration);
      const plannedImplDurationAmountProps: ControlProps = ControlProps.of(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_AMOUNT);
      plannedImplDurationAmountProps.withDefaultValue(this.time.plannedImplDurationAmountControl.value);
      plannedImplDurationAmountProps.withValue(jobTemplate?.plannedImplDuration ? plannedImplDuration.time : null);
      this.time.setControlValue(plannedImplDurationAmountProps);

      const plannedImplDurationUnitProps: ControlProps = ControlProps.of(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_UNIT);
      plannedImplDurationUnitProps.withDefaultValue(this.time.plannedImplDurationUnitControl.value);
      plannedImplDurationUnitProps.withValue(jobTemplate?.plannedImplDuration ? plannedImplDuration.unit : null);
      this.time.setControlValue(plannedImplDurationUnitProps);
    }

    if (jobTemplate?.isIdentical === true || jobTemplate?.isIdentical === false) {
      this.time.isIdenticalControl.setValue(jobTemplate.isIdentical);
    } else {
      this.time.isIdenticalControl.setValue(null);
    }

    if (jobTemplate?.standstill === true || jobTemplate?.standstill === false) {
      this.time.standstillControl.setValue(jobTemplate.standstill);
    } else {
      this.time.standstillControl.setValue(null);
    }
  }

  private fillBasicInfos(jobTemplate: JobTemplateModel) {
    if (!!jobTemplate?.name) {
      const nameProp = ControlProps.of(JobRoutineBasicInfoFormModel.NAME);
      nameProp.withDefaultValue(this.infos.titleControl.value);
      nameProp.withValue(jobTemplate.name);
      this.infos.setControlValue(nameProp);
    }

    if (jobTemplate?.isPinned === true || jobTemplate?.isPinned === false) {
      const isPinnedProp = ControlProps.of(JobRoutineBasicInfoFormModel.IS_PINNED);
      isPinnedProp.withDefaultValue(this.infos.isPinnedControl.value);
      isPinnedProp.withValue(jobTemplate.isPinned);
      this.infos.setControlValue(isPinnedProp);
    }


    if (jobTemplate?.inHouseExecution === true || jobTemplate?.inHouseExecution === false) {
      const executionLocationExtern = ControlProps.of(JobRoutineBasicInfoFormModel.EXECUTION_LOCATION_EXTERN);
      executionLocationExtern.withDefaultValue(this.infos.executionLocationExternControl.value);
      executionLocationExtern.withValue(!jobTemplate.inHouseExecution);
      this.infos.setControlValue(executionLocationExtern);
    }

    if (!!jobTemplate?.flatRate) {
      const flatRateProp = ControlProps.of(JobRoutineBasicInfoFormModel.FLAT_RATE);
      flatRateProp.withDefaultValue(this.infos.flatRateControl.value);
      flatRateProp.withValue(jobTemplate.flatRate);
      this.infos.setControlValue(flatRateProp);
    }

    if (!!jobTemplate?.producerNumber) {
      const producerNumberProp = ControlProps.of(JobRoutineBasicInfoFormModel.PRODUCER_NUMBER);
      producerNumberProp.withDefaultValue(this.infos.producerNumberControl.value);
      producerNumberProp.withValue(jobTemplate.producerNumber);
      this.infos.setControlValue(producerNumberProp);
    }

    if (!!jobTemplate?.description) {
      const descriptionProp = ControlProps.of(JobRoutineBasicInfoFormModel.DESCRIPTION);
      descriptionProp.withDefaultValue(this.infos.descriptionControl.value);
      descriptionProp.withValue(jobTemplate.description);
      this.infos.setControlValue(descriptionProp);
    }

    if (jobTemplate?.jobType) {
      const categoryProp = ControlProps.of(JobRoutineBasicInfoFormModel.CATEGORY);
      categoryProp.withDefaultValue(this.infos.categoryControl.value);
      categoryProp.withValue(jobTemplate.jobType);
      this.infos.setControlValue(categoryProp);
    }

    if (jobTemplate?.serviceProvider) {
      const responsibilityExternProp = ControlProps.of(JobRoutineBasicInfoFormModel.RESPONSIBILITY_EXTERN);
      responsibilityExternProp.withDefaultValue(this.infos.responsibilityExternControl.value);
      responsibilityExternProp.withValue(jobTemplate.serviceProvider != null);
      this.infos.setControlValue(responsibilityExternProp);
    }

    if (jobTemplate?.jobBeginStatus) {
      const jobBeginStatusProp = ControlProps.of(JobRoutineBasicInfoFormModel.JOB_BEGIN_STATUS);
      jobBeginStatusProp.withDefaultValue(this.infos.jobBeginStatusControl.value);
      jobBeginStatusProp.withValue(JobRoutineBasicInfoFormModel.jobStatus.find(e => e.value == JobUtils.getFilterEnumValueOfIntStatus(jobTemplate.jobBeginStatus)));
      this.infos.setControlValue(jobBeginStatusProp);
    }

    const organizationProp = ControlProps.of(JobRoutineBasicInfoFormModel.ORGANIZATION);
    organizationProp.withValue(jobTemplate.ownerOrg);
    this.infos.setControlValue(organizationProp);

    if (jobTemplate?.serviceProvider) {
      const serviceProviderProp = ControlProps.of(JobRoutineBasicInfoFormModel.SERVICE_PROVIDER);
      serviceProviderProp.withValue(jobTemplate.serviceProvider);
      this.infos.setControlValue(serviceProviderProp);
      this.infos.assignedUserControl.setValidators(null);
      this.infos.assignedWorkGroupControl.setValidators(null);
    }

    const executionServiceProviderProp = ControlProps.of(JobRoutineBasicInfoFormModel.EXECUTION_SERVICE_PROVIDER);
    executionServiceProviderProp.withValue(jobTemplate.executionPlaceServiceProvider);
    this.infos.setControlValue(executionServiceProviderProp);

    const assignedUserProp = ControlProps.of(JobRoutineBasicInfoFormModel.ASSIGNED_USER);
    assignedUserProp.withValue(jobTemplate.assigneeUser);
    this.infos.setControlValue(assignedUserProp);

    const assignedWorkGroupProp = ControlProps.of(JobRoutineBasicInfoFormModel.ASSIGNED_WORK_GROUP);
    assignedWorkGroupProp.withValue(jobTemplate.assigneeGroup);
    this.infos.setControlValue(assignedWorkGroupProp);

    const assigneeTypeProp = ControlProps.of(JobRoutineBasicInfoFormModel.ASSIGNEE_TYPE);
    assigneeTypeProp.withValue(JobRoutineBasicInfoFormModel
      .getAssigneeTypeItem(jobTemplate.assigneeGroup ? AssigneeEntityType.WORKGROUP : AssigneeEntityType.USER));
    this.infos.setControlValue(assigneeTypeProp);
  }

  createJobroutineCreationView(): JobTemplateCreationModel {
      const jobRoutineFormModel = JobTemplateCreationModel.empty();

      //basic infos
      const isModuleTarget: boolean = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET_TYPE).value.value === TargetEntityType.MODULE;
      const isObjectTarget: boolean = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET_TYPE).value.value === TargetEntityType.OBJECT;

      jobRoutineFormModel.type = this.infos.form.get(JobRoutineBasicInfoFormModel.TYPE).value?.value;
      jobRoutineFormModel.isPinned = this.infos.form.get(JobRoutineBasicInfoFormModel.IS_PINNED).value;
      jobRoutineFormModel.isActive = this.infos.form.get(JobRoutineBasicInfoFormModel.ACTIVE).value;
      jobRoutineFormModel.name = this.infos.form.get(JobRoutineBasicInfoFormModel.NAME).value;
      jobRoutineFormModel.description = this.infos.form.get(JobRoutineBasicInfoFormModel.DESCRIPTION).value;
      jobRoutineFormModel.jobTypeId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.CATEGORY).value, WrappedObjectsEnum.ID);
      jobRoutineFormModel.assigneeUserId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.ASSIGNED_USER).value, WrappedObjectsEnum.ID);
      jobRoutineFormModel.assigneeGroupId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.ASSIGNED_WORK_GROUP).value, WrappedObjectsEnum.ID);
      jobRoutineFormModel.serviceProviderId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.SERVICE_PROVIDER).value, WrappedObjectsEnum.ID);
      jobRoutineFormModel.flatRate = this.infos.form.get(JobRoutineBasicInfoFormModel.FLAT_RATE).value;
      jobRoutineFormModel.producerNumber = this.infos.form.get(JobRoutineBasicInfoFormModel.PRODUCER_NUMBER).value;
      jobRoutineFormModel.inHouseExecution = !this.infos.form.get(JobRoutineBasicInfoFormModel.EXECUTION_LOCATION_EXTERN).value;
      jobRoutineFormModel.externExecutionSpId = this.infos.form.get(JobRoutineBasicInfoFormModel.EXECUTION_SERVICE_PROVIDER).value?.id;

      const beginStatus = this.infos.form.get(JobRoutineBasicInfoFormModel.JOB_BEGIN_STATUS).value;
      if (beginStatus?.value) {
          jobRoutineFormModel.jobBeginStatus = JobUtils.getIntValueOfStatus(beginStatus.value);
      } else {
          jobRoutineFormModel.jobBeginStatus = 1;
      }

      if (isModuleTarget) {
          jobRoutineFormModel.moduleId = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET).value.id;
      } else if (isObjectTarget) {
          jobRoutineFormModel.componentId = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET).value.id;
      }

      //region time
      const durationUnitInMillis = this.time.getDurationMillis(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_UNIT);
      const plannedImplDurationUnitInMillis = this.time.getDurationMillis(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_UNIT);
      const plannedWorkingTime = this.time.form.get(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT).value;

      jobRoutineFormModel.plannedExecutionAmount = this.time.form.get(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT).value;
      jobRoutineFormModel.plannedWorkingTime = plannedWorkingTime ? plannedWorkingTime * durationUnitInMillis : plannedWorkingTime;

      jobRoutineFormModel.plannedImplDuration = this.time.form.get(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_AMOUNT).value;
      jobRoutineFormModel.plannedImplDuration *= plannedImplDurationUnitInMillis;

      jobRoutineFormModel.standstill = this.time.form.get(JobRoutineTimeFormModel.TIME_STAND_STILL).value;
      jobRoutineFormModel.isIdentical = this.time.form.get(JobRoutineTimeFormModel.TIME_IS_IDENTICAL).value;
      //endregion time

      //region files
      const docs: DocumentModel[] = this.files.form.get(JobRoutineFilesFormModel.DOCUMENTS).value;
      if (docs?.length) {
          jobRoutineFormModel.documents = docs
              .map(doc => DocumentGroupModel.initGroupFromDocument(doc, Languages.availableLanguages, DocumentGroupReferenceType.JOB_ROUTINE_TEMPLATE, null));
      } else {
          jobRoutineFormModel.documents = [];
      }
      //endregion files

      return jobRoutineFormModel;
  }

    createJobroutineRelatedEntitiesCreationView(jobroutineId: string ,fromParent = false): JobTemplateCreationModel {
        const jobRoutineFormModel = JobTemplateCreationModel.empty();
        jobRoutineFormModel.id = jobroutineId;

        return jobRoutineFormModel;
    }




  createCreationView(fromParent = false): JobTemplateCreationModel {
    const jobRoutineFormModel = JobTemplateCreationModel.empty();

    //basic infos
    const isModuleTarget: boolean = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET_TYPE).value.value === TargetEntityType.MODULE;
    const isObjectTarget: boolean = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET_TYPE).value.value === TargetEntityType.OBJECT;

    jobRoutineFormModel.isPinned = this.infos.form.get(JobRoutineBasicInfoFormModel.IS_PINNED).value;
    jobRoutineFormModel.isActive = this.infos.form.get(JobRoutineBasicInfoFormModel.ACTIVE).value;
    jobRoutineFormModel.name = this.infos.form.get(JobRoutineBasicInfoFormModel.NAME).value;
    jobRoutineFormModel.description = this.infos.form.get(JobRoutineBasicInfoFormModel.DESCRIPTION).value;
    jobRoutineFormModel.jobTypeId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.CATEGORY).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.assigneeUserId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.ASSIGNED_USER).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.assigneeGroupId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.ASSIGNED_WORK_GROUP).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.serviceProviderId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.SERVICE_PROVIDER).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.flatRate = this.infos.form.get(JobRoutineBasicInfoFormModel.FLAT_RATE).value;
    jobRoutineFormModel.producerNumber = this.infos.form.get(JobRoutineBasicInfoFormModel.PRODUCER_NUMBER).value;
    jobRoutineFormModel.inHouseExecution = !this.infos.form.get(JobRoutineBasicInfoFormModel.EXECUTION_LOCATION_EXTERN).value;
    jobRoutineFormModel.externExecutionSpId = this.infos.form.get(JobRoutineBasicInfoFormModel.EXECUTION_SERVICE_PROVIDER).value?.id;

    const beginStatus = this.infos.form.get(JobRoutineBasicInfoFormModel.JOB_BEGIN_STATUS).value;
    if (beginStatus?.value) {
      jobRoutineFormModel.jobBeginStatus = JobUtils.getIntValueOfStatus(beginStatus.value);
    } else {
      jobRoutineFormModel.jobBeginStatus = 1;
    }

    if (isModuleTarget) {
      jobRoutineFormModel.moduleId = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET).value.id;
    } else if (isObjectTarget) {
      jobRoutineFormModel.componentId = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET).value.id;
    }

    //region time
    const durationUnitInMillis = this.time.getDurationMillis(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_UNIT);
    const plannedImplDurationUnitInMillis = this.time.getDurationMillis(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_UNIT);
    const plannedWorkingTime = this.time.form.get(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT).value;

    jobRoutineFormModel.plannedExecutionAmount = this.time.form.get(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT).value;
    jobRoutineFormModel.plannedWorkingTime = plannedWorkingTime ? plannedWorkingTime * durationUnitInMillis : plannedWorkingTime;

    jobRoutineFormModel.plannedImplDuration = this.time.form.get(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_AMOUNT).value;
    jobRoutineFormModel.plannedImplDuration *= plannedImplDurationUnitInMillis;

    jobRoutineFormModel.standstill = this.time.form.get(JobRoutineTimeFormModel.TIME_STAND_STILL).value;
    jobRoutineFormModel.isIdentical = this.time.form.get(JobRoutineTimeFormModel.TIME_IS_IDENTICAL).value;
    //endregion time

    //region files
    const docs: DocumentModel[] = this.files.form.get(JobRoutineFilesFormModel.DOCUMENTS).value;
    if (docs?.length) {
      jobRoutineFormModel.documents = docs
        .map(doc => DocumentGroupModel.initGroupFromDocument(doc, Languages.availableLanguages, DocumentGroupReferenceType.JOB_ROUTINE_TEMPLATE, null));
    } else {
      jobRoutineFormModel.documents = [];
    }
    //endregion files

    return jobRoutineFormModel;
  }

  createJobroutineTemplateCreationView(): JobTemplateCreationModel {
    const jobRoutineFormModel = JobTemplateCreationModel.empty();

    //basic infos
    const isModuleTarget: boolean = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET_TYPE).value.value === TargetEntityType.MODULE;
    const isObjectTarget: boolean = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET_TYPE).value.value === TargetEntityType.OBJECT;

    jobRoutineFormModel.type = this.infos.form.get(JobRoutineBasicInfoFormModel.TYPE).value?.value;
    jobRoutineFormModel.isPinned = this.infos.form.get(JobRoutineBasicInfoFormModel.IS_PINNED).value;
    jobRoutineFormModel.isActive = this.infos.form.get(JobRoutineBasicInfoFormModel.ACTIVE).value;
    jobRoutineFormModel.name = this.infos.form.get(JobRoutineBasicInfoFormModel.NAME).value;
    jobRoutineFormModel.description = this.infos.form.get(JobRoutineBasicInfoFormModel.DESCRIPTION).value;
    jobRoutineFormModel.jobTypeId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.CATEGORY).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.assigneeUserId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.ASSIGNED_USER).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.assigneeGroupId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.ASSIGNED_WORK_GROUP).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.serviceProviderId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.SERVICE_PROVIDER).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.flatRate = this.infos.form.get(JobRoutineBasicInfoFormModel.FLAT_RATE).value;
    jobRoutineFormModel.producerNumber = this.infos.form.get(JobRoutineBasicInfoFormModel.PRODUCER_NUMBER).value;
    jobRoutineFormModel.inHouseExecution = !this.infos.form.get(JobRoutineBasicInfoFormModel.EXECUTION_LOCATION_EXTERN).value;
    jobRoutineFormModel.externExecutionSpId = this.infos.form.get(JobRoutineBasicInfoFormModel.EXECUTION_SERVICE_PROVIDER).value?.id;

    const beginStatus = this.infos.form.get(JobRoutineBasicInfoFormModel.JOB_BEGIN_STATUS).value;
    if (beginStatus?.value) {
      jobRoutineFormModel.jobBeginStatus = JobUtils.getIntValueOfStatus(beginStatus.value);
    } else {
      jobRoutineFormModel.jobBeginStatus = 1;
    }

    if (isModuleTarget) {
      jobRoutineFormModel.moduleId = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET).value.id;
    } else if (isObjectTarget) {
      jobRoutineFormModel.componentId = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET).value.id;
    }

    //region time
    const durationUnitInMillis = this.time.getDurationMillis(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_UNIT);
    const plannedImplDurationUnitInMillis = this.time.getDurationMillis(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_UNIT);
    const plannedWorkingTime = this.time.form.get(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT).value;

    jobRoutineFormModel.plannedExecutionAmount = this.time.form.get(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT).value;
    jobRoutineFormModel.plannedWorkingTime = plannedWorkingTime ? plannedWorkingTime * durationUnitInMillis : plannedWorkingTime;

    jobRoutineFormModel.plannedImplDuration = this.time.form.get(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_AMOUNT).value;
    jobRoutineFormModel.plannedImplDuration *= plannedImplDurationUnitInMillis;

    jobRoutineFormModel.standstill = this.time.form.get(JobRoutineTimeFormModel.TIME_STAND_STILL).value;
    jobRoutineFormModel.isIdentical = this.time.form.get(JobRoutineTimeFormModel.TIME_IS_IDENTICAL).value;
    //endregion time

    //region files
    const docs: DocumentModel[] = this.files.form.get(JobRoutineFilesFormModel.DOCUMENTS).value;
    if (docs?.length) {
      jobRoutineFormModel.documents = docs
        .map(doc => DocumentGroupModel.initGroupFromDocument(doc, Languages.availableLanguages, DocumentGroupReferenceType.JOB_ROUTINE_TEMPLATE, null));
    } else {
      jobRoutineFormModel.documents = [];
    }
    //endregion files

    return jobRoutineFormModel;
  }

  createUpdateView(id: string): JobTemplateUpdateModel {
    const jobRoutineFormModel = new JobTemplateUpdateModel();

    jobRoutineFormModel.id = id;
    //region basic infos
    const isModuleTarget: boolean = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET_TYPE).value.value === TargetEntityType.MODULE;
    const isObjectTarget: boolean = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET_TYPE).value.value === TargetEntityType.OBJECT;

    jobRoutineFormModel.isActive = this.infos.form.get(JobRoutineBasicInfoFormModel.ACTIVE).value;
    jobRoutineFormModel.isPinned = this.infos.form.get(JobRoutineBasicInfoFormModel.IS_PINNED).value;
    jobRoutineFormModel.name = this.infos.form.get(JobRoutineBasicInfoFormModel.NAME).value;
    jobRoutineFormModel.description = this.infos.form.get(JobRoutineBasicInfoFormModel.DESCRIPTION).value;
    jobRoutineFormModel.jobTypeId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.CATEGORY).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.assigneeUserId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.ASSIGNED_USER).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.assigneeGroupId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.ASSIGNED_WORK_GROUP).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.serviceProviderId = this.checkForWrappedObject(this.infos.form.get(JobRoutineBasicInfoFormModel.SERVICE_PROVIDER).value, WrappedObjectsEnum.ID);
    jobRoutineFormModel.flatRate = this.infos.form.get(JobRoutineBasicInfoFormModel.FLAT_RATE).value;
    jobRoutineFormModel.producerNumber = this.infos.form.get(JobRoutineBasicInfoFormModel.PRODUCER_NUMBER).value;
    jobRoutineFormModel.inHouseExecution = !this.infos.form.get(JobRoutineBasicInfoFormModel.EXECUTION_LOCATION_EXTERN).value;
    jobRoutineFormModel.externExecutionServiceProviderId = this.infos.form.get(JobRoutineBasicInfoFormModel.EXECUTION_SERVICE_PROVIDER).value?.id;


    const beginStatus = this.infos.form.get(JobRoutineBasicInfoFormModel.JOB_BEGIN_STATUS).value;
    if (beginStatus?.value) {
      jobRoutineFormModel.jobBeginStatus = JobUtils.getIntValueOfStatus(beginStatus.value);
    } else {
      jobRoutineFormModel.jobBeginStatus = 1;
    }

    if (isModuleTarget) {
      jobRoutineFormModel['moduleId'] = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET).value.id;
    } else if (isObjectTarget) {
      jobRoutineFormModel['componentId'] = this.infos.form.get(JobRoutineBasicInfoFormModel.TARGET).value.id;
    }
    //endregion basic infos

    //region time
    const durationUnitInMillis = this.time.getDurationMillis(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_UNIT);
    const plannedImplDurationUnitInMillis = this.time.getDurationMillis(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_UNIT);

    jobRoutineFormModel.plannedWorkingTime = this.time.form.get(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT).value;
    if (jobRoutineFormModel.plannedWorkingTime) {
      jobRoutineFormModel.plannedWorkingTime *= durationUnitInMillis;
    } else {
      jobRoutineFormModel.plannedWorkingTime = null;
    }

    jobRoutineFormModel.plannedImplDuration = this.time.form.get(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_AMOUNT).value;
    jobRoutineFormModel.plannedImplDuration *= plannedImplDurationUnitInMillis;

    jobRoutineFormModel.standstill = this.time.form.get(JobRoutineTimeFormModel.TIME_STAND_STILL).value;
    jobRoutineFormModel.isIdentical = this.time.form.get(JobRoutineTimeFormModel.TIME_IS_IDENTICAL).value;
    //endregion time

    //region files
    const docs: DocumentModel[] = this.files.form.get(JobRoutineFilesFormModel.DOCUMENTS).value;
    if (docs?.length > 0) {
      jobRoutineFormModel.documents = docs
        .map(doc => DocumentGroupModel.initGroupFromDocument(doc, Languages.availableLanguages, DocumentGroupReferenceType.JOB_ROUTINE_TEMPLATE, id));
    } else {
      jobRoutineFormModel.documents = [];
    }
    //endregion files

    return jobRoutineFormModel;
  }
}
